import { Calendar, Pause, Pencil, Play, Save, X } from "lucide-react"
import { getCampaignStatusNameAndClass, formatCampaignType } from "utils/campaignUtils"
import { EsporaiTooltip } from "components"

const CampaignDetailsHeader = ({
  campaign,
  platform,
  onStatusChangeRequest,
  statusLoading = false,
  saveLoading = false,
  onSetEditMode,
  editMode,
  onSave,
  onClose,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Sin definir"
    return new Date(dateString).toLocaleDateString()
  }

  const startDate = formatDate(campaign?.start_time || campaign?.start_date)
  const endDate = formatDate(campaign?.stop_time || campaign?.end_date)
  const statusInfo = getCampaignStatusNameAndClass(campaign?.status)
  const campaignType = formatCampaignType(campaign.campaign_type)
  const isCampaignCreatedByEsporai = campaign?.is_esporai_campaign
  const isMeta = platform === "meta"
  const disableEditWithTooltip = !isCampaignCreatedByEsporai && isMeta

  const PLATFORM_STATUS_MAP = {
    google: {
      ACTIVE: "ENABLED",
      PAUSED: "PAUSED",
    },
    meta: {
      ACTIVE: "ACTIVE",
      PAUSED: "PAUSED",
    },
  }

  const CURRENT_STATUS = campaign?.status
  const STATUS_MAP = PLATFORM_STATUS_MAP[platform] || {}

  const IS_ACTIVE = CURRENT_STATUS === STATUS_MAP.ACTIVE
  const IS_PAUSED = CURRENT_STATUS === STATUS_MAP.PAUSED

  const handleEditToggle = () => {
    if (editMode && onSave) {
      onSave()
    } else {
      onSetEditMode(true)
    }
  }

  return (
    <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl p-4 sm:p-5 mb-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold text-white break-words whitespace-normal">
              {campaign.name} <span className="text-sm text-white/70">({campaignType})</span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/70">
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium break-words whitespace-normal ${
              statusInfo.className.includes('green') 
                ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                : statusInfo.className.includes('yellow')
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/30'
                : 'bg-white/10 text-white/70 border border-white/20'
            }`}>
              {statusInfo.name}
            </span>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-fuchsia-400" />
              <span>{startDate} - {endDate}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end md:items-start">
          {disableEditWithTooltip ? (
            <EsporaiTooltip content="No se puede editar esta campaña de Meta ya que no está creada por Esporai">
              <button
                disabled
                className="px-3 py-1.5 rounded-lg text-sm flex items-center bg-white/10 text-white/50 cursor-not-allowed border border-white/20"
              >
                <Pencil className="h-4 w-4 mr-1.5" />
                <span>Editar</span>
              </button>
            </EsporaiTooltip>
          ) : (
            <button
              onClick={handleEditToggle}
              disabled={saveLoading}
              className={`px-3 py-1.5 rounded-lg text-sm flex items-center transition-all ${
                editMode
                  ? saveLoading
                    ? "bg-gradient-to-r from-fuchsia-500/70 to-blue-600/70 text-white cursor-not-allowed"
                    : "bg-gradient-to-r from-fuchsia-500 to-blue-600 text-white hover:from-fuchsia-600 hover:to-blue-700 cursor-pointer"
                  : "bg-white/10 text-white/70 hover:bg-white/20 cursor-pointer border border-white/20"
              }`}
            >
              {editMode ? (
                <>
                  <Save className="h-4 w-4 mr-1.5" />
                  <span>{saveLoading ? "Guardando..." : "Guardar"}</span>
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-1.5" />
                  <span>Editar</span>
                </>
              )}
            </button>
          )}
          {editMode && (
            <button
              onClick={onClose}
              disabled={saveLoading}
              className="px-3 py-1.5 rounded-lg text-sm flex items-center bg-white/10 text-white/70 hover:bg-white/20 cursor-pointer border border-white/20"
            >
              <X className="h-4 w-4 mr-1.5" />
              <span>Cancelar</span>
            </button>
          )}
          {IS_ACTIVE && !editMode && (
            <button
              onClick={() => onStatusChangeRequest(STATUS_MAP.PAUSED)}
              disabled={statusLoading}
              className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center border ${
                statusLoading
                  ? "bg-white/10 text-white/50 cursor-not-allowed border-white/20"
                  : "bg-white/10 text-white/70 hover:bg-white/20 cursor-pointer border-white/20"
              }`}
            >
              <Pause className="h-4 w-4 mr-1.5" />
              <span>{statusLoading ? "Pausando..." : "Pausar"}</span>
            </button>
          )}
          {IS_PAUSED && !editMode && (
            <button
              onClick={() => onStatusChangeRequest(STATUS_MAP.ACTIVE)}
              disabled={statusLoading}
              className={`px-3 py-1.5 rounded-lg text-sm transition-all flex items-center ${
                statusLoading
                  ? "bg-gradient-to-r from-fuchsia-500/70 to-blue-600/70 text-white cursor-not-allowed"
                  : "bg-gradient-to-r from-fuchsia-500 to-blue-600 text-white hover:from-fuchsia-600 hover:to-blue-700 cursor-pointer"
              }`}
            >
              <Play className="h-4 w-4 mr-1.5" />
              <span>{statusLoading ? "Activando..." : "Activar"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default CampaignDetailsHeader
